import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  Alert,
  AlertTitle,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Badge,
  Popover,
  Tab,
  Tabs,
  Divider,
  Stack,
  LinearProgress,
  Button,
} from '@mui/material';
import {
  Memory as MemoryIcon,
  Speed as SpeedIcon,
  DeviceThermostat as TempIcon,
  Storage as StorageIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  NetworkWifi as NetworkIcon,
  Dvr as GpuIcon,
  Memory as VramIcon,
  NetworkCheck as ReceivedIcon,
  Upload as SentIcon,
  Notifications as NotificationsIcon,
  Dashboard as DashboardIcon,
  Memory as CpuIcon,
  Storage as DiskIcon,
  ArrowBack as ArrowBackIcon,
  ListAlt as LogsIcon,
  Terminal as TerminalIcon,
} from '@mui/icons-material';
import { CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer, AreaChart, Area, XAxis, YAxis } from 'recharts';
import { useTheme } from '@mui/material/styles';
import { getMetricsByNodeId, getAllContainerDetails, getContainerMetrics } from '../api/services/monitoring/Monitoring';
import TerminalModal from './TerminalModal';

// Format number with appropriate unit suffix
const formatNumber = (value, unit) => {
  if (value === undefined || value === null) return '0';
  
  // For percentages, just show 1 decimal place
  if (unit === '%') return `${value.toFixed(1)}${unit}`;
  
  // Format based on size
  if (value < 1) return `${value.toFixed(2)}${unit}`;
  if (value < 10) return `${value.toFixed(1)}${unit}`;
  return `${Math.round(value)}${unit}`;
};

const normalizePercentage = (value) => {
  if (value === undefined || value === null) return 0;
  let normalized = parseFloat(value);
  
  if (normalized > 100) {
    normalized = normalized / 10;
  }
  if (normalized > 100) {
    normalized = normalized / 10;
  }
  
  return Math.min(Math.max(normalized, 0), 100);
};

// Custom tooltip for charts that shows formatted values
const CustomTooltip = ({ active, payload, label, unit, theme }) => {
  if (active && payload && payload.length) {
    return (
      <Paper sx={{ p: 1, bgcolor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)' }}>
        <Typography variant="body2">{label}</Typography>
        {payload.map((entry, index) => (
          <Typography key={index} variant="body2" sx={{ color: entry.color }}>
            {entry.name}: {formatNumber(entry.value, unit || '')}
          </Typography>
        ))}
      </Paper>
    );
  }
  return null;
};

const MetricCard = React.memo(({ icon, title, value, total, unit, percentValue, color, secondaryText, theme }) => (
  <Card sx={{ height: '100%', boxShadow: theme.shadows[1], position: 'relative', overflow: 'visible' }}>
    <CardContent sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box sx={{ 
          p: 1, 
          borderRadius: '50%', 
          bgcolor: `${color}15`,
          mr: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {icon}
        </Box>
        <Typography variant="body1" fontWeight="medium">{title}</Typography>
      </Box>
      
      <Typography variant="h4" sx={{ fontWeight: 'medium', mb: 1, color: theme.palette.text.primary }}>
        {formatNumber(value, unit)}
      </Typography>
      
      {secondaryText && (
        <Typography variant="body2" color="text.secondary">
          {secondaryText}
        </Typography>
      )}
      
      {percentValue !== undefined && (
        <Box sx={{ mt: 1 }}>
          <LinearProgress 
            variant="determinate" 
            value={percentValue} 
            sx={{ 
              height: 6, 
              borderRadius: 3,
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
              '& .MuiLinearProgress-bar': {
                bgcolor: color,
              }
            }} 
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {formatNumber(percentValue, '%')} used
          </Typography>
        </Box>
      )}
    </CardContent>
  </Card>
));

const CompactChart = React.memo(({ data, dataKey, color, title, unit, currentValue, secondaryValue, icon, theme }) => (
  <Card sx={{ 
    height: '100%', 
    width: '100%',
    boxShadow: theme.shadows[1], 
    display: 'flex', 
    flexDirection: 'column' 
  }}>
    <CardContent sx={{ 
      p: 2, 
      flex: '1 1 auto',
      display: 'flex', 
      flexDirection: 'column',
      height: '100%' 
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ 
            p: 1, 
            borderRadius: '50%', 
            bgcolor: `${color}15`, 
            mr: 1,
            display: 'flex'
          }}>
            {icon}
          </Box>
          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{title}</Typography>
        </Box>
        <Typography variant="h6" sx={{ color: color, fontWeight: 'medium' }}>
          {formatNumber(currentValue, unit)}
        </Typography>
      </Box>

      {secondaryValue && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {secondaryValue}
        </Typography>
      )}

      <Box sx={{ flex: 1, mt: 'auto', height: 130, minHeight: 130 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={`color${dataKey}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.8}/>
                <stop offset="95%" stopColor={color} stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis 
              dataKey="time" 
              stroke={theme.palette.text.secondary} 
              tick={{ fontSize: 10 }} 
              tickFormatter={(val) => val}
            />
            <YAxis 
              stroke={theme.palette.text.secondary}
              tickFormatter={(value) => formatNumber(value, '')}
              tick={{ fontSize: 10 }}
              width={30}
              domain={['dataMin', 'dataMax']}
              {...(dataKey === 'cpu' || dataKey.includes('Util') ? { domain: [0, 100] } : {})}
              {...(dataKey === 'memory' ? { domain: [0, 100] } : {})}
            />
            <ChartTooltip content={<CustomTooltip unit={unit} theme={theme} />} />
            <Area 
              type="monotone" 
              dataKey={dataKey}
              name={title}
              stroke={color} 
              fillOpacity={1} 
              fill={`url(#color${dataKey})`} 
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </CardContent>
  </Card>
));

export default function Monitoring() {
  const params = useParams();
  const { nodeId, serverId, hostname } = params;
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [metricsData, setMetricsData] = useState([]);
  const [lastMetrics, setLastMetrics] = useState({
    cpu: { usage: 0, cores: 0 },
    memory: { total: 0, free: 0, utilization: 0 },
    disk: { total: 0, free: 0, utilization: 0 },
    network: { received: 0, sent: 0 },
    gpu: {
      count: 0,
      vendor: '',
      models: [],
      temperatures: [],
      utilizations: [],
      vramUtilizations: [],
      memoryClocks: [],
      coreClocks: [],
    }
  });
  const [isOnline, setIsOnline] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [containerData, setContainerData] = useState([]);
  const [containerLoading, setContainerLoading] = useState(false);
  const [containerError, setContainerError] = useState(null);
  const [terminalModalOpen, setTerminalModalOpen] = useState(false);
  const [selectedContainer, setSelectedContainer] = useState(null);
  
  const streamReaderRef = useRef(null);
  const readerActiveRef = useRef(true);
  const pendingMetricsRef = useRef({});
  const lastUpdateTimeRef = useRef(0);
  const updateIntervalRef = useRef(null);
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAlertClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleAlertClose = () => {
    setAnchorEl(null);
  };
  
  const openAlerts = Boolean(anchorEl);

  const handleContainerClick = (container) => {
    setSelectedContainer(container);
    setTerminalModalOpen(true);
  };

  const handleTerminalModalClose = () => {
    setTerminalModalOpen(false);
    setSelectedContainer(null);
  };

  useEffect(() => {
    const targetNodeId = nodeId || serverId || hostname;
    if (!targetNodeId) {
      setIsLoading(true);
      navigate('/monitoring/d76af964-905a-404c-b4ef-050f9259a5a9', { replace: true });
      return;
    }
    setIsLoading(false);
  }, [nodeId, serverId, hostname, navigate]);

  useEffect(() => {
    const currentNodeId = nodeId || serverId;
    if (!currentNodeId) return;

    const setupMetricsStream = async () => {
      try {
        if (streamReaderRef.current) await streamReaderRef.current.cancel().catch(e => console.log('Error closing reader:', e));
        const { streamReader, error } = await getMetricsByNodeId(currentNodeId);
        if (error || !streamReader) {
          console.error('Error setting up metrics stream:', error || 'No stream reader');
          setIsOnline(false);
          return;
        }
        setIsOnline(true);
        streamReaderRef.current = streamReader;
        readerActiveRef.current = true;
        readMetricsStream(streamReader);
      } catch (e) {
        console.error('Exception in metrics streaming setup:', e);
        setIsOnline(false);
      }
    };

    const readMetricsStream = async (reader) => {
      try {
        while (readerActiveRef.current) {
          const { done, value } = await reader.read();
          if (done) break;

          try {
            if (value && value.result) {
              // Additional validation for the data structure
              if (value.result.metrics || value.result.message || value.result.outputType) {
                processMetric(value);
              } else {
                // Skip unknown data structures
                continue;
              }
            }
          } catch (parseError) {
            // Silently handle parsing errors for individual data points
            console.debug('Skipping malformed data point in main metrics stream:', parseError.message);
            continue;
          }
        }
      } catch (e) {
        console.error('Error reading from metrics stream:', e);
        setIsOnline(false);
        if (readerActiveRef.current) setTimeout(setupMetricsStream, 5000);
      }
    };

    const processMetric = (data) => {
      try {
        if (!data || !data.result) return;

        // Handle different types of data
        if (data.result.metrics) {
          const metric = data.result.metrics;

          // Validate metric data
          if (!metric || typeof metric !== 'object') return;

          const type = metric.type;
          if (!type) return;

      // Store metric in pending batch
      pendingMetricsRef.current[type] = metric;

      // Throttle updates to every 2 seconds to prevent overwhelming the UI
      const now = Date.now();
      if (now - lastUpdateTimeRef.current < 2000) return;
      
      lastUpdateTimeRef.current = now;
      processBatchedMetrics();
        } else if (data.result.message || data.result.outputType) {
          // Handle status messages silently
          return;
        }
      } catch (error) {
        // Silently handle processing errors
        console.debug('Error processing metric data:', error.message);
      }
    };

    const processBatchedMetrics = () => {
      const currentTime = new Date();
      const time = currentTime.toLocaleTimeString('en-US', { 
        hour12: true, 
        hour: '2-digit', 
        minute: '2-digit', 
        second: '2-digit' 
      });

      const pendingMetrics = { ...pendingMetricsRef.current };
      pendingMetricsRef.current = {}; // Clear pending metrics

      setLastMetrics(prev => {
        const newMetrics = { ...prev };
        const chartUpdates = {};

        // Process system metrics
        if (pendingMetrics.cpu) {
          const cpuUsage = normalizePercentage(pendingMetrics.cpu.floatValue);
          newMetrics.cpu.usage = cpuUsage;
          chartUpdates.cpu = cpuUsage;
        }

        // Memory metrics
        let memoryUpdated = false;
        if (pendingMetrics.memory_total) {
          newMetrics.memory.total = parseInt(pendingMetrics.memory_total.intValue) / 1024;
          memoryUpdated = true;
        }
        if (pendingMetrics.memory_free) {
          newMetrics.memory.free = parseInt(pendingMetrics.memory_free.intValue) / 1024;
          memoryUpdated = true;
        }
        if (memoryUpdated && newMetrics.memory.total > 0) {
              const util = ((newMetrics.memory.total - newMetrics.memory.free) / newMetrics.memory.total) * 100;
          newMetrics.memory.utilization = util;
          chartUpdates.memory = util;
        }

        // Disk metrics
        let diskUpdated = false;
        if (pendingMetrics.disk_total) {
          newMetrics.disk.total = parseInt(pendingMetrics.disk_total.intValue);
          diskUpdated = true;
        }
        if (pendingMetrics.disk_free) {
          newMetrics.disk.free = parseInt(pendingMetrics.disk_free.intValue);
          diskUpdated = true;
        }
        if (diskUpdated && newMetrics.disk.total > 0) {
              const util = ((newMetrics.disk.total - newMetrics.disk.free) / newMetrics.disk.total) * 100;
          newMetrics.disk.utilization = util;
          chartUpdates.disk = util;
        }

        // Network metrics
        if (pendingMetrics.network_received) {
          const received = pendingMetrics.network_received.floatValue / (1024 * 1024);
          newMetrics.network.received = received;
          chartUpdates.networkReceived = received;
        }
        if (pendingMetrics.network_sent) {
          const sent = pendingMetrics.network_sent.floatValue / (1024 * 1024);
          newMetrics.network.sent = sent;
          chartUpdates.networkSent = sent;
        }

        // GPU metrics
        if (pendingMetrics.gpu_count) {
          newMetrics.gpu.count = pendingMetrics.gpu_count.floatValue || 1;
        }
        if (pendingMetrics.gpu_vendor) {
          newMetrics.gpu.vendor = pendingMetrics.gpu_vendor.stringValue;
        }

        // Initialize GPU arrays if needed
                ['models', 'temperatures', 'utilizations', 'vramUtilizations', 'memoryClocks', 'coreClocks'].forEach(prop => {
                  if (!newMetrics.gpu[prop]) newMetrics.gpu[prop] = [];
                });

        // Process all GPUs
        for (let i = 0; i < newMetrics.gpu.count; i++) {
          if (pendingMetrics[`gpu_model_${i}`]) {
            newMetrics.gpu.models[i] = pendingMetrics[`gpu_model_${i}`].stringValue;
          }
          if (pendingMetrics[`gpu_temperature_${i}`]) {
            newMetrics.gpu.temperatures[i] = pendingMetrics[`gpu_temperature_${i}`].floatValue;
            chartUpdates[`gpuTemp${i}`] = pendingMetrics[`gpu_temperature_${i}`].floatValue;
          }
          if (pendingMetrics[`gpu_utilization_${i}`]) {
            const usage = normalizePercentage(pendingMetrics[`gpu_utilization_${i}`].floatValue);
            newMetrics.gpu.utilizations[i] = usage;
            chartUpdates[`gpuUtil${i}`] = usage;
          }
          if (pendingMetrics[`gpu_vram_utilization_${i}`]) {
            const vram = normalizePercentage(pendingMetrics[`gpu_vram_utilization_${i}`].floatValue * 100);
            newMetrics.gpu.vramUtilizations[i] = vram;
            chartUpdates[`gpuVram${i}`] = vram;
          }
          if (pendingMetrics[`gpu_mclk_${i}`]) {
            newMetrics.gpu.memoryClocks[i] = pendingMetrics[`gpu_mclk_${i}`].stringValue;
          }
          if (pendingMetrics[`gpu_sclk_${i}`]) {
            newMetrics.gpu.coreClocks[i] = pendingMetrics[`gpu_sclk_${i}`].stringValue;
          }
        }

        // Single batch update to chart data
        if (Object.keys(chartUpdates).length > 0) {
          updateChartData(time, chartUpdates);
        }

        return newMetrics;
      });
    };

    const updateChartData = (time, metrics) => {
      setMetricsData(prevData => {
        const existingIndex = prevData.findIndex(point => point.time === time);
        if (existingIndex >= 0) {
          const newData = [...prevData];
          newData[existingIndex] = { ...newData[existingIndex], ...metrics };
          return newData;
        } else {
          // Keep only last 8 data points to reduce memory usage
          const newData = [...prevData, { time, ...metrics }];
          return newData.slice(-8);
        }
      });
    };

    setupMetricsStream();
    return () => {
      readerActiveRef.current = false;
      if (streamReaderRef.current) streamReaderRef.current.cancel().catch(e => console.log('Error cancelling stream:', e));
      streamReaderRef.current = null;
      // Clear pending metrics to prevent memory leaks
      pendingMetricsRef.current = {};
      lastUpdateTimeRef.current = 0;
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
        updateIntervalRef.current = null;
      }
    };
  }, [nodeId, serverId]);

  // Fetch container data
  useEffect(() => {
    const currentNodeId = nodeId || serverId;
    if (!currentNodeId) return;

    const fetchContainerData = async () => {
      try {
        setContainerLoading(true);
        setContainerError(null);

        const { data, error } = await getAllContainerDetails(currentNodeId);

        if (error) {
          setContainerError(error);
          console.error('Container API error:', error);
        } else {
          console.log('Container data received:', data);
          // Handle the actual API response structure: {containerInfo: [...]}
          const containerArray = (data && data.containerInfo && Array.isArray(data.containerInfo))
                                ? data.containerInfo
                                : (Array.isArray(data) ? data : []);
          setContainerData(containerArray);
        }
      } catch (error) {
        console.error('Error fetching container data:', error);
        setContainerError(error.message);
      } finally {
        setContainerLoading(false);
      }
    };

    fetchContainerData();
  }, [nodeId, serverId]);


  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 3 }}>Loading monitoring dashboard...</Typography>
      </Box>
    );
  }

  const renderSummaryCards = () => (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          icon={<CpuIcon sx={{ color: theme.palette.primary.main }} />}
          title="CPU Usage"
          value={lastMetrics?.cpu?.usage || 0}
          unit="%"
          percentValue={lastMetrics?.cpu?.usage || 0}
          color={theme.palette.primary.main}
          theme={theme}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          icon={<MemoryIcon sx={{ color: theme.palette.info.main }} />}
          title="Memory"
          value={lastMetrics?.memory?.total - lastMetrics?.memory?.free || 0}
          unit="GB"
          percentValue={lastMetrics?.memory?.utilization || 0}
          color={theme.palette.info.main}
          secondaryText={`${formatNumber(lastMetrics?.memory?.total || 0, 'GB')} total`}
          theme={theme}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          icon={<DiskIcon sx={{ color: theme.palette.success.main }} />}
          title="Disk"
          value={lastMetrics?.disk?.total - lastMetrics?.disk?.free || 0}
          unit="GB"
          percentValue={lastMetrics?.disk?.utilization || 0}
          color={theme.palette.success.main}
          secondaryText={`${formatNumber(lastMetrics?.disk?.total || 0, 'GB')} total`}
          theme={theme}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          icon={<NetworkIcon sx={{ color: theme.palette.warning.main }} />}
          title="Network"
          value={lastMetrics?.network?.received || 0}
          unit="GB"
          color={theme.palette.warning.main}
          secondaryText={`${formatNumber(lastMetrics?.network?.sent || 0, 'GB')} sent`}
          theme={theme}
        />
      </Grid>
    </Grid>
  );

  const renderChartSection = (title, charts) => (
    <Box sx={{ mb: 4 }}>
      {title && <Typography variant="h6" sx={{ mb: 2 }}>{title}</Typography>}
      <Grid container spacing={2} alignItems="stretch">
        {charts.map((chart, index) => (
          <Grid item key={index} xs={12} md={chart.width || (charts.length === 2 ? 6 : 4)} sx={{ height: 270 }}>
            <CompactChart 
              data={metricsData} 
              dataKey={chart.dataKey} 
              color={chart.color} 
              title={chart.title} 
              unit={chart.unit} 
              currentValue={chart.value} 
              secondaryValue={chart.secondaryText} 
              icon={chart.icon}
              theme={theme}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderSystemMetrics = () => (
    <Box>
      {renderSummaryCards()}
      
      {renderChartSection("System Performance", [
        {
          dataKey: "cpu",
          color: theme.palette.primary.main,
          title: "CPU Usage",
          unit: "%",
          value: lastMetrics?.cpu?.usage || 0,
          icon: <SpeedIcon sx={{ color: theme.palette.primary.main }} />
        },
        {
          dataKey: "memory",
          color: theme.palette.info.main,
          title: "Memory Usage",
          unit: "%",
          value: lastMetrics?.memory?.utilization || 0,
          secondaryText: `${formatNumber(lastMetrics?.memory?.free || 0, 'GB')} free / ${formatNumber(lastMetrics?.memory?.total || 0, 'GB')} total`,
          icon: <MemoryIcon sx={{ color: theme.palette.info.main }} />
        }
      ])}
      
      {renderChartSection("Storage & Network", [
        {
          dataKey: "disk",
          color: theme.palette.success.main,
          title: "Disk Space",
          unit: "%",
          value: lastMetrics?.disk?.utilization || 0,
          secondaryText: `${formatNumber(lastMetrics?.disk?.free || 0, 'GB')} free / ${formatNumber(lastMetrics?.disk?.total || 0, 'GB')} total`,
          icon: <StorageIcon sx={{ color: theme.palette.success.main }} />
        },
        {
          dataKey: "networkReceived",
          color: theme.palette.warning.main,
          title: "Network Received",
          unit: "GB",
          value: lastMetrics?.network?.received || 0,
          icon: <ReceivedIcon sx={{ color: theme.palette.warning.main }} />
        },
        {
          dataKey: "networkSent",
          color: theme.palette.error.main,
          title: "Network Sent",
          unit: "GB",
          value: lastMetrics?.network?.sent || 0,
          icon: <SentIcon sx={{ color: theme.palette.error.main }} />
        }
      ])}
    </Box>
  );

  const renderGpuMetrics = () => {
    const hasGpuData = lastMetrics.gpu.count > 0 && (lastMetrics.gpu.models.length > 0 || lastMetrics.gpu.temperatures.length > 0 || lastMetrics.gpu.utilizations.length > 0);
    if (!hasGpuData) return <Alert severity="info">No GPU metrics available for this node.</Alert>; 

    return (
      <Box>
        {/* GPU Overview */}
        <Card sx={{ mb: 3, p: 2, bgcolor: theme.palette.background.paper }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            GPU Overview ({lastMetrics.gpu.count} GPUs)
          </Typography>
          <Grid container spacing={1}>
            {Array.from({ length: lastMetrics.gpu.count }).map((_, i) => {
              const gpuModel = lastMetrics.gpu.models[i] || `GPU ${i}`;
              const gpuVendor = lastMetrics.gpu.vendor || '';
              return (
                <Grid item xs={12} sm={6} md={4} key={`gpu-overview-${i}`}>
                  <Box sx={{
                    p: 1.5,
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    textAlign: 'center'
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                      {gpuModel} {gpuVendor ? `(${gpuVendor})` : ''}
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                      <Box>
                        <Typography variant="caption" color="text.secondary">Temp</Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.error.main, fontWeight: 'medium' }}>
                          {lastMetrics.gpu.temperatures[i] || 0}°C
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Util</Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 'medium' }}>
                          {lastMetrics.gpu.utilizations[i] || 0}%
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">VRAM</Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 'medium' }}>
                          {lastMetrics.gpu.vramUtilizations[i] || 0}%
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Card>

        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          GPU Charts
        </Typography>
        {Array.from({ length: lastMetrics.gpu.count }).map((_, i) => {
          const gpuModel = lastMetrics.gpu.models[i] || `GPU ${i}`;
          const gpuVendor = lastMetrics.gpu.vendor || '';

          return (
            <Box key={`gpu-section-${i}`} sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: theme.palette.primary.main }}>
                GPU {i} - {gpuModel} {gpuVendor ? `(${gpuVendor})` : ''}
              </Typography>

              {renderChartSection("", [
                {
                  dataKey: `gpuTemp${i}`,
                  color: theme.palette.error.main,
                  title: "Temperature",
                  unit: "°C",
                  value: lastMetrics.gpu.temperatures[i] || 0,
                  icon: <TempIcon sx={{ color: theme.palette.error.main }} />
                },
                {
                  dataKey: `gpuUtil${i}`,
                  color: theme.palette.primary.main,
                  title: "Utilization",
                  unit: "%",
                  value: lastMetrics.gpu.utilizations[i] || 0,
                  icon: <GpuIcon sx={{ color: theme.palette.primary.main }} />
                },
                {
                  dataKey: `gpuVram${i}`,
                  color: theme.palette.secondary.main,
                  title: "VRAM",
                  unit: "%",
                  value: lastMetrics.gpu.vramUtilizations[i] || 0,
                  icon: <VramIcon sx={{ color: theme.palette.secondary.main }} />
                }
              ])}
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderLogs = () => {
    if (containerLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading container logs...</Typography>
        </Box>
      );
    }

    if (containerError) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load container information: {containerError}
        </Alert>
      );
    }

    if (!containerData || !Array.isArray(containerData) || containerData.length === 0) {
      return (
        <Alert severity="info">
          No container information available for this node.
        </Alert>
      );
    }

    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
          Docker Container Details ({containerData.length} containers)
        </Typography>

        <Grid container spacing={2}>
          {containerData.map((container, index) => {
            // Ensure container is an object
            if (!container || typeof container !== 'object') return null;

            return (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    borderLeft: `4px solid ${container.status?.toLowerCase() === 'running' ? theme.palette.success.main : theme.palette.error.main}`,
                    transition: 'all 0.2s ease-in-out',
                    position: 'relative',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                    }
                  }}
                >

                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                        {container.containerName || `Container ${index + 1}`}
                      </Typography>
                      <Chip
                        label={container.status || 'unknown'}
                        size="small"
                        color={container.status?.toLowerCase() === 'running' ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        <strong>ID:</strong> {container.containerId?.substring(0, 12) || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        <strong>Image:</strong> {container.imageName || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        <strong>Network:</strong> {container.networkMode || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        <strong>Created:</strong> {container.createdAt ? new Date(container.createdAt).toLocaleString() : 'N/A'}
                      </Typography>
                    </Box>

                  {container.ports && Array.isArray(container.ports) && container.ports.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'medium' }}>
                        Ports:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {container.ports.map((port, portIndex) => {
                          // Handle different port formats: [port1, port2] or {PublicPort, PrivatePort}
                          if (typeof port === 'number') {
                            return (
                              <Chip
                                key={portIndex}
                                label={port.toString()}
                                size="small"
                                variant="outlined"
                              />
                            );
                          } else if (typeof port === 'object' && port !== null) {
                            const portLabel = port.PublicPort && port.PrivatePort
                              ? `${port.PublicPort}:${port.PrivatePort}`
                              : (port.PrivatePort || port.PublicPort || 'N/A');
                            return (
                              <Chip
                                key={portIndex}
                                label={portLabel}
                                size="small"
                                variant="outlined"
                              />
                            );
                          }
                          return null;
                        })}
                      </Box>
                    </Box>
                  )}

                  <Box sx={{ mt: 'auto', pt: 2 }}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<TerminalIcon />}
                      onClick={() => handleContainerClick(container)}
                      sx={{
                        width: '100%',
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        '&:hover': {
                          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.main} 100%)`,
                        }
                      }}
                    >
                      View Metrics
                    </Button>
                  </Box>

                </CardContent>
              </Card>
            </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  return (
    <Box sx={{
      px: 2,
      pt: 2,
      pb: 2,
      minHeight: '100vh',
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary
    }}>
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: theme.shadows[2] }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DashboardIcon sx={{ fontSize: 28, mr: 1.5, color: theme.palette.primary.main }} />
            <Typography variant="h5">Server Monitoring</Typography>
            <Chip 
              label={isOnline ? "Online" : "Offline"} 
              color={isOnline ? "success" : "error"} 
              icon={isOnline ? <CheckIcon /> : <WarningIcon />} 
              size="small"
              sx={{ ml: 2 }}
            />
          </Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/cloud-management')}
            sx={{ ml: 2 }}
          >
            Back to Cloud Management
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Node ID: {nodeId || serverId} | Updates every 2 seconds | Showing all {lastMetrics.gpu.count > 0 ? lastMetrics.gpu.count + ' GPUs' : 'system metrics'}
        </Typography>
        {!isOnline && <Alert severity="error" sx={{ mt: 2 }} variant="filled">Unable to connect to monitoring service.</Alert>}
      </Paper>
      
      <Paper sx={{ borderRadius: 2, boxShadow: theme.shadows[2] }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              py: 2
            }
          }}
        >
          <Tab label="System" icon={<CpuIcon />} iconPosition="start" />
          <Tab label="GPU" icon={<GpuIcon />} iconPosition="start" />
          <Tab label="Logs" icon={<LogsIcon />} iconPosition="start" />
        </Tabs>
        
        <Box sx={{ p: 3 }}>
          {activeTab === 0 && renderSystemMetrics()}
          {activeTab === 1 && renderGpuMetrics()}
          {activeTab === 2 && renderLogs()}
        </Box>
      </Paper>
      
      <Popover 
        open={openAlerts} 
        anchorEl={anchorEl} 
        onClose={handleAlertClose} 
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}} 
        transformOrigin={{vertical: 'top', horizontal: 'right'}}
      >
        <Paper sx={{ width: 380, maxWidth: '100%' }}>
          <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6">Recent Alerts</Typography>
          </Box>
          <List sx={{ py: 0 }}>
            <ListItem>
              <Alert severity="warning" sx={{ width: '100%' }} variant="outlined">
                <AlertTitle>High CPU Usage</AlertTitle>
                CPU utilization exceeded 80% for 5 minutes
                <Typography variant="caption" display="block">29 minutes ago</Typography>
              </Alert>
            </ListItem>
            <ListItem>
              <Alert severity="info" sx={{ width: '100%' }} variant="outlined">
                <AlertTitle>Disk Space Warning</AlertTitle>
                Disk space below 20% free
                <Typography variant="caption" display="block">51 minutes ago</Typography>
              </Alert>
            </ListItem>
          </List>
        </Paper>
      </Popover>

      <TerminalModal
        open={terminalModalOpen}
        onClose={handleTerminalModalClose}
        containerName={selectedContainer?.containerName}
        nodeId={nodeId || serverId}
      />

      <style jsx>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
      `}</style>
    </Box>
  );
}
